import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// User interface
interface User {
  id: string;
  email: string;
  full_name: string;
  subscription_status: string;
  created_at: string;
}

// Auth response interface
interface AuthResponse {
  success: boolean;
  message?: string;
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  register: (userData: {
    email: string;
    password: string;
    full_name: string;
    preferred_language?: string;
    location?: { city: string };
  }) => Promise<AuthResponse>;
  logout: () => void;
}

// Auth provider props interface
interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState<boolean>(true);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get('/api/v1/auth/me');
          setUser(response.data.data);
        } catch (error) {
          console.error('❌ Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    try {
      console.log('🔑 Frontend login attempt:', { email });
      
      const response = await axios.post('/api/v1/auth/login', {
        email,
        password
      });

      console.log('✅ Frontend login response:', response.data);

      const { token: newToken, data: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      
      console.log('✅ User logged in successfully:', userData);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Frontend login error:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    full_name: string;
    preferred_language?: string;
    location?: { city: string };
  }): Promise<AuthResponse> => {
    try {
      console.log('🔐 Frontend registration attempt:', { email: userData.email, name: userData.full_name });
      
      const response = await axios.post('/api/v1/auth/register', userData);

      console.log('✅ Frontend registration response:', response.data);

      const { token: newToken, data: responseUserData } = response.data;
      
      setToken(newToken);
      setUser(responseUserData);
      localStorage.setItem('token', newToken);
      
      console.log('✅ User registered successfully:', responseUserData);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Frontend registration error:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = (): void => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 